import { Typography } from '@yiluhub/ui-elements';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import Card from 'modules/landing-page/components/card';
import { usePageLoad } from 'modules/landing-page/hooks/usePageLoad';
import { EventParam } from 'modules/landing-page/utils/types';

import './styles.scss';

enum Images {
  AirportTransfers = 'airport-transfers.jpg',
  AirportParking = 'airport-parking.jpg',
  AirportLounge = 'airport-lounges.jpg',
  AirportTaxi = 'airport-taxi.jpg',
  ESim = 'e-sim.jpg',
  VipService = 'VIP-services.jpeg',
  Tickets = 'tickets.jpeg',
  MeetGreet = 'meet-greet.jpeg',
  FastLane = 'fast-lane.jpeg',
  BannerImage = 'banner-image.jpg',
  BannerImageMobile = 'banner-image-mobile.jpg',
}

const combineImageWithBasePath = (imageName: Images) => {
  const assetsUrl =
    'https://provider-resources-production-v2.s3.eu-central-1.amazonaws.com/landingpage/assets/';
  return `${assetsUrl}${imageName}`;
};

const TOUCHPOINT_TYPE = 'landing_page';
const PRODUCT_CARD_ORDER = 'AP-AL-AT-RI-SIM-FT-HPM-HPV-CS';

export const LandingPageBody: React.FC = () => {
  // fire load event once page loads
  const { handleClickCallback } = usePageLoad({
    touchpointType: TOUCHPOINT_TYPE,
    productCardOrder: PRODUCT_CARD_ORDER,
  });

  const { t } = useTranslation();

  const location = useLocation();

  const touchpointUrlParam = new URLSearchParams(location.search).get('touchpoint');
  const TRACKING_PARAM = touchpointUrlParam
    ? `?touchpoint=lp_${touchpointUrlParam}`
    : '?touchpoint=landing_page';

  return (
    <>
      <section className="lp-container">
        <div className="lp-header-search-box">
          <Typography variant="h1" className="lp-heading" bold={true}>
            {t('landingpage.searchbox.title.text')}
          </Typography>
          <Typography variant="h5" className="lp-subheading">
            {t('landingpage.searchbox.subtitle.text')}
          </Typography>
        </div>
        <div className="lp-header-image">
          <picture>
            <source
              media="(max-width: 500px"
              srcSet={combineImageWithBasePath(Images.BannerImageMobile)}
            />
            <source
              media="(max-width: 900px"
              srcSet={combineImageWithBasePath(Images.BannerImage)}
            />
            <img src={combineImageWithBasePath(Images.BannerImage)} alt="header" />
          </picture>
        </div>
        <div className="lp-header-text-container">
          <Typography variant="p2" className="lp-header-text_text" bold={true}>
            {t('landingpage.herosection.subtitle.text')}
          </Typography>
        </div>
      </section>
      <section className="lp-cards-section">
        <div className="lp-cards-container">
          <Card
            dataTestId="airport-parking"
            heading={t('landingpage.card.airportparking.heading.text')}
            subHeading={t('landingpage.card.airportparking.subHeading.text')}
            image={combineImageWithBasePath(Images.AirportParking)}
            price={t('landingpage.card.generic.booknow.text')}
            linkUrl={`https://partneroffer.discover-airlines.com/parking/search${TRACKING_PARAM}`}
            handleClick={(e) => {
              e.preventDefault?.();
              handleClickCallback(EventParam.CLICK_AP);
            }}
          />
          <Card
            dataTestId="airport-lounges"
            heading={t('landingpage.card.airportlounge.heading.text')}
            subHeading={t('landingpage.card.airportlounge.subHeading.text')}
            image={combineImageWithBasePath(Images.AirportLounge)}
            price={t('landingpage.card.generic.booknow.text')}
            linkUrl={`https://partneroffer.discover-airlines.com/lounges/search${TRACKING_PARAM}`}
            handleClick={(e) => {
              e.preventDefault?.();
              handleClickCallback(EventParam.CLICK_AL);
            }}
          />
          <Card
            dataTestId="esim"
            heading={t('landingpage.card.esim.heading.text')}
            subHeading={t('landingpage.card.esim.subHeading.text')}
            image={combineImageWithBasePath(Images.ESim)}
            price={t('landingpage.card.generic.booknow.text')}
            linkUrl={`https://www.discover-airlines.com/en/information/additional-services/esim/${TRACKING_PARAM}`}
            handleClick={(e) => {
              e.preventDefault?.();
              handleClickCallback(EventParam.CLICK_SIM);
            }}
          />
          <Card
            dataTestId="fastlane"
            heading={t('landingpage.card.fastlane.heading.text')}
            subHeading={t('landingpage.card.fastlane.subHeading.text')}
            image={combineImageWithBasePath(Images.FastLane)}
            price={t('landingpage.card.generic.booknow.text')}
            linkUrl={`https://partneroffer.discover-airlines.com/fasttrack/product${TRACKING_PARAM}`}
            handleClick={(e) => {
              e.preventDefault?.();
              handleClickCallback(EventParam.CLICK_FT);
            }}
          />
        </div>
      </section>
    </>
  );
};

const LandingPage: React.FC = () => {
  return (
    <div className="lp-layout">
      <LandingPageBody />
    </div>
  );
};

export default LandingPage;
